import { useEffect, useMemo, useState } from 'react';
import {
  alpha,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import WorkspacesIcon from '@mui/icons-material/Workspaces';
import { HideSupportStyle } from 'components/shared/HideSupportStyle';
import { GroupColorPicker } from './GroupColorPicker';

const HEX6_RE = /^#[0-9a-fA-F]{6}$/;
const NAME_MAX = 40;

export interface EditGroupDialogProps {
  open: boolean;
  /** Title shown on the dialog (e.g. "New group" or "Edit group"). */
  title: string;
  /** Pre-populated name. Empty string for create mode. */
  initialName: string;
  /** Pre-populated `#RRGGBB` color. */
  initialColor: string;
  onSubmit: (name: string, color: string) => void;
  onClose: () => void;
}

/**
 * Shared form for creating and editing a symbol group. Live preview at
 * the top, then name and color sections with quick-pick swatches and a
 * full hex picker for fine control.
 *
 * Client-side validation mirrors the backend's contract:
 *   - name: 1-40 chars after trim, non-empty
 *   - color: matches /^#[0-9a-fA-F]{6}$/
 */
export const EditGroupDialog = ({
  open,
  title,
  initialName,
  initialColor,
  onSubmit,
  onClose,
}: EditGroupDialogProps) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [name, setName] = useState(initialName);
  const [color, setColor] = useState(initialColor);

  // Reset every time the dialog re-opens so a stale draft from a prior
  // open doesn't leak into a fresh edit.
  useEffect(() => {
    if (open) {
      setName(initialName);
      setColor(initialColor);
    }
  }, [open, initialName, initialColor]);

  const trimmed = name.trim();
  const nameError = useMemo(() => {
    if (trimmed.length === 0) {
      return 'Name is required';
    }
    if (trimmed.length > NAME_MAX) {
      return `Max ${NAME_MAX} characters`;
    }
    return null;
  }, [trimmed]);
  const colorError = HEX6_RE.test(color) ? null : 'Use a #RRGGBB hex color';
  const canSubmit = nameError == null && colorError == null;

  const handleSubmit = () => {
    if (!canSubmit) {
      return;
    }
    onSubmit(trimmed, color.toLowerCase());
  };

  const previewColor = HEX6_RE.test(color) ? color : theme.palette.divider;
  const hasName = trimmed.length > 0;
  const previewName = hasName ? trimmed : 'Untitled group';

  return (
    <>
      {open && <HideSupportStyle />}
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="xs"
        fullWidth
        fullScreen={fullScreen}
        PaperProps={{
          sx: {
            borderRadius: fullScreen ? 0 : 2.5,
          },
        }}
      >
        <DialogTitle>{title}</DialogTitle>
        <Stack sx={{ ml: '24px', mb: 0.5, pr: 3 }}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontSize: 13, lineHeight: 1.5 }}
          >
            Link components together so a symbol change on one syncs to all
            members of the group.
          </Typography>
        </Stack>

        <DialogContent>
          <Stack sx={{ gap: '24px' }}>
            {/* Live preview card */}
            <Stack
              sx={{
                flexDirection: 'row',
                alignItems: 'center',
                gap: 3,
                p: 3,
                borderRadius: 1.5,
                border: `1px solid ${alpha(previewColor, 0.4)}`,
                backgroundColor: alpha(previewColor, 0.08),
                transition: 'background-color 150ms, border-color 150ms',
              }}
            >
              <WorkspacesIcon
                sx={{ color: previewColor, fontSize: 22, flexShrink: 0 }}
              />
              <Stack sx={{ flex: 1, minWidth: 0, gap: 1 }}>
                <Typography
                  component="div"
                  variant="caption"
                  sx={{
                    fontSize: 12,
                    color: theme.palette.text.secondary,
                    lineHeight: 1.3,
                  }}
                >
                  Preview
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    fontSize: 14,
                    color: hasName
                      ? theme.palette.text.primary
                      : theme.palette.text.disabled,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    fontStyle: hasName ? 'normal' : 'italic',
                    lineHeight: 1.3,
                  }}
                >
                  {previewName}
                </Typography>
              </Stack>
            </Stack>

            {/* Name */}
            <Stack sx={{ gap: 2 }}>
              <SectionLabel
                title="Name"
                hint="A short label that describes the group's purpose."
              />
              <TextField
                autoFocus
                placeholder="e.g. Earnings watch"
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && canSubmit) {
                    e.preventDefault();
                    handleSubmit();
                  }
                }}
                error={name.length > 0 && nameError != null}
                helperText={
                  name.length > 0 ? nameError : `${trimmed.length}/${NAME_MAX}`
                }
                inputProps={{ maxLength: NAME_MAX }}
                size="small"
                fullWidth
                FormHelperTextProps={{
                  sx: { fontSize: 11, ml: 0.5, mt: 0.5 },
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 1.5,
                    fontSize: 14,
                    '& fieldset': {
                      borderColor: alpha(theme.palette.primary.main, 0.5),
                    },
                    '&:hover fieldset': {
                      borderColor: alpha(theme.palette.primary.main, 0.8),
                    },
                    '&.Mui-focused fieldset': {
                      borderWidth: 1,
                    },
                  },
                }}
              />
            </Stack>

            {/* Color */}
            <Stack sx={{ gap: 3 }}>
              <SectionLabel
                title="Color"
                hint="Pick a preset or enter a custom hex."
              />
              <GroupColorPicker value={color} onChange={setColor} />
            </Stack>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button
            onClick={onClose}
            sx={{ textTransform: 'none', fontWeight: 500, minWidth: 80 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={!canSubmit}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              minWidth: 80,
              boxShadow: 'none',
              '&:hover': { boxShadow: 'none' },
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

/**
 * Small overline label + hint pair used between sections of the dialog.
 * Kept inline to avoid creating a separate file for a single-use helper.
 */
const SectionLabel = ({ title, hint }: { title: string; hint: string }) => {
  const theme = useTheme();
  return (
    <Box>
      <Typography
        variant="caption"
        component="div"
        sx={{
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: 0.6,
          textTransform: 'uppercase',
          color: theme.palette.text.primary,
          mb: 0.25,
          lineHeight: 1.3,
        }}
      >
        {title}
      </Typography>
      <Typography
        variant="caption"
        component="div"
        sx={{
          fontSize: 12,
          color: theme.palette.text.secondary,
          lineHeight: 1.4,
        }}
      >
        {hint}
      </Typography>
    </Box>
  );
};
